import {
  CheckoutDetails,
  CustomerPreOrderCheckout,
  OrderFormat,
  OutletListItem,
  PaginationMeta,
  PreOrderAttendee,
  PreOrderCheckoutDetails,
  PreOrderLink,
  PreOrderSettings,
  PreOrderStatus,
} from '@ancon/wildcat-types'
import { EntityState } from '@reduxjs/toolkit'

export type PreOrderPageQuery = {
  preOrderId?: string
}

export type GroupOrderPageQuery = {
  groupOrderId?: string
}

export type PreOrderStatusPageQuery = {
  outletId?: string
  preOrderId?: string
  /** Whether or not redirected from payment completion */
  redirect?: boolean
}

export enum PreOrderStartStep {
  First = 1,
  Second,
}

export type PreOrderReducerState = {
  /* Pre order link details */
  preOrderLink: PreOrderLink | null

  /* First or second step (Required for host) */
  startStep: PreOrderStartStep

  /* Pre order host information modal visible state  */
  showPreOrderHostInfoModal: boolean

  /** Current pre-order checkout */
  preOrderCheckout: PreOrderCheckoutDetails | null
  preOrderSelectedAttendeeId: string | null
  preOrderCheckoutFetchPending: boolean
  /** The latest checkout fetch, so an older response can't overwrite it */
  preOrderCheckoutFetchRequestId: string | null
  preOrderAddItemPending: boolean
  preOrderAttendeeDeletePending: boolean
  preOrderConfirmPending: boolean
  preOrderUpdatePending: boolean
  preOrderValidatePending: boolean
  isPreOrderLeaveModalVisible: boolean
  isPreOrderExistWarningModalVisible: boolean
  isPreOrderTimeChangeModalVisible: boolean

  /** Pre-order checkout summary */
  preOrderCheckoutSummary: PreOrderCheckoutDetails | null
  preOrderCheckoutSummaryFetchPending: boolean
  preOrderSummaryOutletListItem: OutletListItem | null

  /** Current pre-order user */
  preOrderUser: PreOrderUser | null

  /* Host pre-order manage page */
  isMobilePreOrderMembersManageStep: boolean
  isPreOrderAttendeeRemoveModalVisible: boolean
  isAttendanceConfirmationModalVisible: boolean
  isAttendeeEmptyItemsWarningModalVisible: boolean

  preOrderCheckoutStatusLastUpdatedAt: number

  /**  WL company user dashboard */
  orderCalendarSelectedDate: string | null
  orderCalendarSelectedMonth: string | null // Start day of the month
  calendarPreOrderList: EntityState<CustomerPreOrderCheckout, string> & {
    isFetchPending: boolean
  }
  newOrderStep: NewOrderStep
  newOrderEditModeStep: NewOrderStep
  newOrder: NewOrderDetails
  /** New company order panel */
  newOrderPanel: NewOrderPanelState
  newGroupOrderCreatePending: boolean
  createdGroupOrderId: string | null
  upcomingOrderList: EntityState<CustomerPreOrderCheckout, string> & {
    isFetchPending: boolean
    hasMore: boolean
    meta: PaginationMeta
  }
  selectedPreOrderId: string | null
  selectedPreOrderListItem: CustomerPreOrderCheckout | null
  selectedPreOrderDetails: PreOrderCheckoutDetails | CheckoutDetails | null
  selectedPreOrderFetchPending: boolean

  /** Pre-order settings */
  preOrderSettings: PreOrderSettings | null
  preOrderSettingsFetchPending: boolean
}

export type AttendeePreOrderStartFormState = {
  name: string
  email: string
}

export type HostPreOrderStartFormState = {
  email: string
  pinCode: string
}

export enum PreOrderUserRole {
  Unknown = 'unknown',
  Host = 'host',
  Attendee = 'attendee',
}

export enum HostPreOrderStartAction {
  EnterPin,
  Activate,
  StartPreOrder,
}

export type PreOrderUser = {
  /** Attendee/Host ID */
  id: string
  email: string
  name?: string
  /** Pre-order host pin code */
  pin?: string
  isHost: boolean
}

export type PreOrderUserTokenPayload = PreOrderUser & {
  expiresAt: number
}

export type PreOrderCheckoutEntityType =
  | 'preOrderCheckout'
  | 'preOrderCheckoutSummary'

export type NewOrderDetails = {
  orderDate: string | null
  orderTime: number | null
  orderFormat: OrderFormat | null
  orderMethod: PreOrderMethod
  groupOrderName: string | null
  groupOrderSize: number | null
}

/** Steps of the new company order panel */
export enum NewOrderPanelStep {
  Restaurant = 'restaurant',
  Format = 'orderFormat',
  Date = 'date',
  Time = 'time',
  OrderType = 'orderType',
}

export type NewOrderPanelStepState = {
  step: NewOrderPanelStep
  /** What was chosen, shown when the step is done */
  value: string | null
  isComplete: boolean
}

export type NewOrderPanelState = {
  isOpen: boolean
  /** The steps in the order they are asked, e.g. date first from the overview */
  stepOrder: NewOrderPanelStep[]
  currentStep: NewOrderPanelStep
  /** Set when a group order was just created, for the confirmation */
  createdGroupOrderStatus: PreOrderStatus | null
}

export enum NewOrderStep {
  None,
  OrderConfiguration,
  OrderMethodSelection,
  HostPreview,
  Summary,
  GroupOrderReceived,
  ShareGroupOrderLink,
}

export enum PreOrderMethod {
  Individual,
  Group,
}

export type GroupOrderFormState = {
  groupOrderName: string
  groupOrderSize: number
}

export type CustomerPreOrderListType =
  | 'upcomingOrderList'
  | 'calendarPreOrderList'

// TODO: Move to wildcat-types once pre-order settings expose the horizon (B1, AI-31315)
export type PreOrderSettingsWithHorizon = PreOrderSettings & {
  /** How many months ahead a company can order */
  companyHorizonMonths?: number | null
}

// TODO: Move to wildcat-types once pre-order details say who confirmed (B3, AI-31317)
export type PreOrderAttendeeWithConfirmedBy = PreOrderAttendee & {
  /** The attendee who confirmed this attendee's food, e.g. the host */
  confirmedByAttendeeId?: string | null
}

/** Where an attendee is with their part of a group order (D7) */
export enum AttendeeProgressStatus {
  NotStarted = 'notStarted',
  AddingItems = 'addingItems',
  Confirmed = 'confirmed',
}
